import React, { useMemo } from "react";
import { Box, Typography, Chip } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import styles from "./lineChart.module.scss";

interface LineChartProps {
  title?: string;
  data?: any[];
  xAxisKey?: string;
  dataKey?: string;
  lineColor?: string;
  legendLabel?: string;
  height?: number;
  valueUnit?: string;
  showThisYear?: boolean;
}

const CustomLineChart: React.FC<LineChartProps> = ({
  title = "Line Chart",
  data = [],
  xAxisKey = "month",
  dataKey = "value",
  lineColor = "#5b6b9e",
  legendLabel = "Value",
  height = 240,
  valueUnit = "days",
  showThisYear = false,
}) => {
  // Calculate dynamic domain and ticks based on data
  const { maxValue, ticks } = useMemo(() => {
    if (!data || data.length === 0) {
      return { maxValue: 100, ticks: [0, 25, 50, 75, 100] };
    }

    const values = data.map((item) => Number(item[dataKey] || 0));
    const max = Math.max(...values, 1);

    // Round up to nearest multiple of 10
    const roundedMax = Math.ceil(max / 10) * 10 + 20;
    const tickInterval = Math.ceil(roundedMax / 4 / 10) * 10;
    const calculatedTicks = Array.from(
      { length: 5 },
      (_, i) => i * tickInterval
    );

    return { maxValue: roundedMax, ticks: calculatedTicks };
  }, [data, dataKey]);

  // Calculate optimal tick interval to show ~10-12 labels
  const xAxisInterval = useMemo(() => {
    if (!data || data.length === 0) return 0;
    const dataLength = data.length;

    // Show all labels if data points are <= 12
    if (dataLength <= 12) return 0;

    // Calculate interval to show approximately 10-12 labels
    return Math.ceil(dataLength / 12) - 1;
  }, [data]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box className={styles.customTooltip}>
          <Typography className={styles.tooltipLabel}>{label}</Typography>
          <Typography
            className={styles.tooltipValue}
            style={{ color: lineColor }}
          >
            {payload[0].value} {valueUnit}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const CustomLegend = () => {
    return (
      <Box className={styles.customLegend}>
        <Box className={styles.legendItem}>
          <Box
            className={styles.legendLine}
            style={{ backgroundColor: lineColor }}
          />
          <Typography className={styles.legendText}>{legendLabel}</Typography>
        </Box>
      </Box>
    );
  };

  if (!data || data.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            {title}
          </Typography>
          {showThisYear && (
            <Chip
              label="This Year"
              size="small"
              sx={{
                backgroundColor: "#e6faf5",
                color: "#08ADC2",
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
          )}
        </Box>
        <Box
          className={styles.chartContainer}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={height}
        >
          <Typography variant="body2" color="textSecondary">
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          {title}
        </Typography>
        {showThisYear && (
          <Chip
            label="This Year"
            size="small"
            sx={{
              backgroundColor: "#e6faf5",
              color: "#08ADC2",
              fontWeight: 500,
              fontSize: "0.75rem",
            }}
          />
        )}
      </Box>

      <Box className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={height}>
          <LineChart
            data={data}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey={xAxisKey}
              tick={{ fill: "#666666", fontSize: 12 }}
              axisLine={{ stroke: "#e0e0e0" }}
              interval={xAxisInterval}
            />
            <YAxis
              tick={{ fill: "#666666", fontSize: 12 }}
              axisLine={{ stroke: "#e0e0e0" }}
              domain={[0, maxValue]}
              ticks={ticks}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey={dataKey}
              stroke={lineColor}
              strokeWidth={2}
              dot={{ fill: lineColor, r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      <CustomLegend />
    </Box>
  );
};

export default CustomLineChart;
